/**
 * The ONE invoice/quote money calculator on the client.
 *
 * Kept deliberately in lock-step with the server's authoritative
 * calculator (xop-brain
 * apps/crm-api/app/models/concerns/invoice_totals.rb). Both use the
 * same CASCADING discount rule so the in-form preview and the saved
 * record always agree:
 *
 *   subTotal            = Σ line amount
 *   discountable        = Σ amount of lines NOT excluded from discount
 *   afterMarket         = discountable × (1 − market% )
 *   afterReseller       = afterMarket  × (1 − reseller% )   ← cascades
 *   additionalValue     = additionalAmount × additionalPct / 100
 *   discountedSubTotal  = max(afterReseller − additionalValue, 0)
 *                         + non-discountable lines
 *   taxAmount           = discountedSubTotal × tax%
 *   totalAmount         = discountedSubTotal + taxAmount
 *
 * Money is rounded to 2 decimals at each persisted boundary, matching
 * the server's BigDecimal `.round(2)`.
 *
 * History: totals used to be stored in form state and recomputed only
 * inside a few event handlers (and never on data-load / catalog-pick /
 * line-removal), while two of those handlers passed arguments in the
 * wrong positional slots. Deriving everything from this one pure
 * function removes that whole class of bug.
 */

export interface InvoiceTotalsLine {
  amount?: number | string | null;
  seats?: number | string | null;
  chargePerSeat?: number | string | null;
  excludeFromDiscount?: boolean;
}

export interface InvoiceTotalsInput {
  products: InvoiceTotalsLine[];
  marketDiscount?: number | null;
  resellerDiscount?: number | null;
  additionalDiscountAmount?: number | null;
  additionalDiscountPercentage?: number | null;
  taxPercentage?: number | null;
}

export interface InvoiceTotals {
  subTotal: number;
  discountableSubTotal: number;
  nonDiscountableSubTotal: number;
  marketDiscountAmount: number;
  resellerDiscountAmount: number;
  additionalDiscountValue: number;
  discountedSubTotal: number;
  taxAmount: number;
  totalAmount: number;
  balanceDue: number;
}

function num(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

function round2(value: number): number {
  // +EPSILON so e.g. 89.725 rounds to 89.73, matching BigDecimal.
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function clampPct(value: number | string | null | undefined): number {
  return Math.min(Math.max(num(value), 0), 100);
}

function lineAmount(line: InvoiceTotalsLine): number {
  const amount = num(line.amount);
  if (amount !== 0) return amount;
  return num(line.seats) * num(line.chargePerSeat);
}

export function computeInvoiceTotals(input: InvoiceTotalsInput): InvoiceTotals {
  const products = Array.isArray(input.products) ? input.products : [];

  let subTotalRaw = 0;
  let discountableRaw = 0;
  for (const line of products) {
    const amt = lineAmount(line);
    subTotalRaw += amt;
    if (!line.excludeFromDiscount) discountableRaw += amt;
  }
  const nonDiscountableRaw = subTotalRaw - discountableRaw;

  const market = clampPct(input.marketDiscount);
  const reseller = clampPct(input.resellerDiscount);
  const addPct = clampPct(input.additionalDiscountPercentage);
  const addAmt = Math.max(num(input.additionalDiscountAmount), 0);

  const afterMarket = discountableRaw * (1 - market / 100);
  const afterReseller = afterMarket * (1 - reseller / 100);
  const additionalDiscountValue = round2((addAmt * addPct) / 100);

  const discountedDiscountable = Math.max(
    afterReseller - additionalDiscountValue,
    0,
  );
  const discountedSubTotal = round2(discountedDiscountable + nonDiscountableRaw);

  const tax = clampPct(input.taxPercentage);
  const taxAmount = round2((discountedSubTotal * tax) / 100);
  const totalAmount = round2(discountedSubTotal + taxAmount);

  return {
    subTotal: round2(subTotalRaw),
    discountableSubTotal: round2(discountableRaw),
    nonDiscountableSubTotal: round2(nonDiscountableRaw),
    marketDiscountAmount: round2(discountableRaw - afterMarket),
    resellerDiscountAmount: round2(afterMarket - afterReseller),
    additionalDiscountValue,
    discountedSubTotal,
    taxAmount,
    totalAmount,
    balanceDue: totalAmount,
  };
}
